/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import isNil from 'lodash/isNil';
import omit from 'lodash/omit';
import noop from 'lodash/noop';
import useDeepCompareEffect from 'use-deep-compare-effect';
import scrollIntoView from 'scroll-into-view-if-needed';
import { List } from 'carbon-addons-iot-react';

const propTypes = {
  /** List heading */
  title: PropTypes.string,
  /** Determines whether the search function is enabled */
  hasSearch: PropTypes.bool,
  /** Determines if multi-select is enabled */
  hasMultiSelect: PropTypes.bool,
  /** Determines if items can be deselected, meaning once an item is selected,
   * it can only be deselected by selecting another item */
  hasDeselection: PropTypes.bool,
  /** Buttons to be presented in List header */
  buttons: PropTypes.arrayOf(PropTypes.node),
  /** ListItems to be displayed */
  // eslint-disable-next-line react/forbid-prop-types
  items: PropTypes.arrayOf(PropTypes.object),
  /** Internationalization text */
  i18n: PropTypes.shape({
    /** Text displayed in search bar */
    searchPlaceHolderText: PropTypes.string,
    expand: PropTypes.string,
    close: PropTypes.string,
    itemsSelected: PropTypes.string,
    move: PropTypes.string,
    cancel: PropTypes.string,
    allRows: PropTypes.string,
    modalTitle: PropTypes.string,
    modalDescription: PropTypes.string,
    loadMore: PropTypes.string,
  }),
  /** Displays the List as full height */
  isFullHeight: PropTypes.bool,
  /** optional skeleton to be rendered while loading data */
  isLoading: PropTypes.bool,
  /** optionally makings each list item a large / fat row */
  isLargeRow: PropTypes.bool,
  /** Item id to be pre-selected */
  defaultSelectedId: PropTypes.string,
  /** Item ids to be pre-expanded */
  defaultExpandedIds: PropTypes.arrayOf(PropTypes.string),
  /** optional classname to be passed to the dom element */
  className: PropTypes.string,
  /** Optional function to be called when item is selected */
  onSelect: PropTypes.func,
  onSearch: PropTypes.func,
  onExpansion: PropTypes.func,
  onExpandedChange: PropTypes.func,
  /** SimplePagination props from addons */
  pagination: PropTypes.shape({
    page: PropTypes.number,
    maxPage: PropTypes.number,
    totalItems: PropTypes.number,
    totalItemsText: PropTypes.string,
    pageOfPagesText: PropTypes.func,
    onPage: PropTypes.func,
    nextPageText: PropTypes.string,
    prevPageText: PropTypes.string,
    pageText: PropTypes.string,
  }),
  /** Display value for search bar */
  defaultSearchValue: PropTypes.string,

  handleLoadMore: PropTypes.func,
  loadingMoreIds: PropTypes.arrayOf(PropTypes.string),
  enableHorizontalScrollbar: PropTypes.bool,
};

const defaultProps = {
  title: null,
  hasSearch: false,
  pagination: null,
  hasMultiSelect: false,
  hasDeselection: true,
  buttons: null,
  i18n: {
    searchPlaceHolderText: 'Enter a value',
    expand: 'Expand',
    close: 'Close',
    itemSelected: '1 item selected',
    itemsSelected: '%d items selected',
    move: 'Move',
    cancel: 'Cancel',
    allRows: 'All rows',
    itemTitle: 'Move 1 item underneath',
    itemsTitle: 'Move %d items underneath',
    modalDescription: 'Select a destination',
    loadMore: 'Load more',
  },
  isFullHeight: false,
  isLoading: false,
  isLargeRow: false,
  defaultSelectedId: null,
  defaultExpandedIds: [],
  className: null,
  items: [],
  onSelect: noop,
  onExpansion: noop,
  onExpandedChange: noop,
  onSearch: noop,
  defaultSearchValue: undefined,
  handleLoadMore: noop,
  loadingMoreIds: [],
  enableHorizontalScrollbar: false,
};

/**
 * Assumes that the first level of items is not searchable and is only uses to categorize the
 * items. Because of that, only search through the children. If a child is found while filtering,
 * it needs to be returned to the filtered array. Deep clone is required because spread syntax is
 * only a shallow clone
 * @param {Array<Object>} items
 * @param {String} value what to search for
 * @returns {Array<Object>}
 */
export const searchForNestedItemIds = (items, value) => {
  const filteredItems = [];
  items.forEach(item => {
    // if the item has children, recurse and search children
    if (item.children) {
      const matchingChildren = searchForNestedItemIds(item.children, value);
      // if it's children did, we still need the item
      if (matchingChildren.length > 0) {
        filteredItems.push({ ...item, children: matchingChildren });
      }
    } // if the item matches, add it to the filterItems array
    else if (item.id === value) {
      filteredItems.push(item);
    }
  });
  return filteredItems;
};

const AsyncList = ({
  title,
  hasSearch,
  pagination,
  hasMultiSelect,
  hasDeselection,
  buttons,
  items,
  i18n,
  isFullHeight,
  isLoading,
  isLargeRow,
  defaultSelectedId,
  defaultExpandedIds,
  className,
  onSearch,
  onSelect,
  onExpansion,
  onExpandedChange,
  defaultSearchValue,
  handleLoadMore,
  loadingMoreIds,
  enableHorizontalScrollbar,
}) => {
  const mergedI18n = useMemo(() => ({ ...defaultProps.i18n, ...i18n }), [i18n]);

  const [expandedIds, setExpandedIds] = useState(defaultExpandedIds);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [localItems, setLocalItems] = useState(items);

  // support asynchronous loads of the default value
  useEffect(() => {
    if (searchValue === '' && !isNil(defaultSearchValue)) {
      setSearchValue(defaultSearchValue);
    }
  }, [defaultSearchValue, searchValue]);

  // we need to remove the react elements to perform useDeepCompareEffects
  const itemsWithoutReactElements = items.map(item => ({
    ...item,
    content: omit(item.content, 'tags'),
    ...(item.children
      ? {
          children: item.children.map(child => ({
            ...child,
            content: omit(child.content, 'tags'),
          })),
        }
      : {}),
  }));

  useDeepCompareEffect(() => {
    setLocalItems(items);
  }, [itemsWithoutReactElements]);

  const selectedItemRef = useCallback(node => {
    if (node && node.parentNode) {
      scrollIntoView(node.parentNode, {
        scrollMode: 'if-needed',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, []);

  const setSelected = id => {
    if (selectedIds.includes(id) && hasDeselection) {
      setSelectedIds(selectedIds.filter(item => item !== id));
      // else, no-op because the item can't be deselected
    } else if (hasMultiSelect) {
      setSelectedIds([...selectedIds, id]);
    } else {
      setSelectedIds([id]);
    }
  };

  const handleSelect = id => {
    setSelected(id);
    // only select if the item is not already selected
    if (onSelect && (!selectedIds.includes(id) || hasDeselection)) {
      onSelect(id);
    }
  };

  useDeepCompareEffect(
    // have to use deep compare to accurately compare items
    () => {
      // Expand the parent elements of the defaultSelectedId
      if (defaultSelectedId) {
        const tempFilteredItems = searchForNestedItemIds(items, defaultSelectedId);
        const tempExpandedIds = [...expandedIds];
        // Expand the categories that have found results
        tempFilteredItems.forEach(categoryItem => {
          tempExpandedIds.push(categoryItem.id);
        });
        setExpandedIds(tempExpandedIds);

        if (!isEqual(selectedIds, [defaultSelectedId])) {
          // If the defaultSelectedId prop is updated from the outside, we need to use it
          setSelected(defaultSelectedId);
        }
      }
    },
    [defaultSelectedId, itemsWithoutReactElements]
  );

  /**
   * Searching via API may be expensive so search by 300ms which is a reasonable amount of
   * time for a single word to be typed.
   */
  const delayedSearch = debounce(textInput => onSearch(textInput), 300);

  return (
    <List
      ref={selectedItemRef}
      className={className}
      title={title}
      buttons={buttons}
      items={localItems}
      i18n={mergedI18n}
      pagination={pagination}
      isFullHeight={isFullHeight}
      isLoading={isLoading}
      isLargeRow={isLargeRow}
      search={
        hasSearch
          ? {
              value: searchValue,
              onChange: evt => {
                setSearchValue(evt.target.value);
                delayedSearch(evt.target.value);
              },
            }
          : null
      }
      expandedIds={expandedIds}
      toggleExpansion={id => {
        if (expandedIds.filter(rowId => rowId === id).length > 0) {
          // remove id from array
          setExpandedIds(expandedIds.filter(rowId => rowId !== id));
          // collapse item
          onExpansion(id, false);
        } else {
          setExpandedIds(expandedIds.concat([id]));
          // expanding item
          onExpansion(id, true);
        }
      }}
      selectedIds={selectedIds}
      loadingMoreIds={loadingMoreIds}
      handleSelect={handleSelect}
      handleLoadMore={handleLoadMore}
      onExpandedChange={onExpandedChange}
      enableHorizontalScrollbar={enableHorizontalScrollbar}
    />
  );
};

AsyncList.propTypes = propTypes;
AsyncList.defaultProps = defaultProps;

export default AsyncList;
